// =============================================================
// Report display formatting — turns the server's RAW report cells (machine ids,
// 0–1 ratios, hour decimals, money) into the polished, human-readable strings the
// Reports screen renders. DISPLAY-ONLY: the server (/reports/:id) stays the single
// source of the numbers; this never re-computes a metric, only formats it.
//
// Field-kind detection is driven off the column KEY the server already declares
// (deviceId / userId / *Hours / lineValue / availability …) plus the metric `unit`
// the server attaches to headline metrics ("h" | "ratio" | "currency"). When a
// column is something we don't recognise (an opaque string already formatted by
// the server, a title, a status), we leave it untouched.
// =============================================================

/** The tenant currency for money columns/metrics (EUR plant — matches Dashboard). */
export const DEFAULT_CURRENCY = "EUR";

/** Maps an entity id → display name; returns null when it can't resolve. */
export type NameResolver = (id: string) => string | null;

/** Column keys the server uses for a DEVICE reference (resolve → device name). */
const DEVICE_KEYS = new Set(["deviceId"]);
/** Column keys the server uses for a USER reference (resolve → user name). */
const USER_KEYS = new Set(["userId", "assigneeId", "performerId"]);

/** The literal the server already emits for an unassigned device (keep verbatim). */
export const UNASSIGNED_LITERAL = "(unassigned)";

/**
 * Is this column key an id-reference column? (deviceId / userId / assigneeId …)
 * Drives id→name resolution at render time.
 */
export function idColumnKind(key: string): "device" | "user" | null {
  if (DEVICE_KEYS.has(key)) return "device";
  if (USER_KEYS.has(key)) return "user";
  return null;
}

/** A 0–1 ratio column/metric (availability / rate / compliance / share). */
function isRatioKey(key: string): boolean {
  return /availability|percent|ratio|rate|compliance|share/i.test(key);
}

/** An hours column (server emits decimal hours under *Hours / *Time keys). */
function isHoursKey(key: string): boolean {
  return /hours$|hours\b|workhours|repairhours|downtimehours/i.test(key) || /\bhours\b/i.test(key);
}

/** A money column (parts-spend line value / spend / cost). */
function isCurrencyKey(key: string): boolean {
  return /linevalue|spend|cost|amount|total(sum|value)?$/i.test(key);
}

/** A date/timestamp column (server emits an ISO-8601 string). */
function isDateKey(key: string): boolean {
  return /at$|date$/i.test(key);
}

/** Format an ISO timestamp string as a locale date (no time noise in tables). */
function formatDate(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value; // not a date — leave verbatim
  return d.toLocaleDateString();
}

/** Format a 0–1 ratio (or already-0..100 percent) as `XX.X%`. */
export function formatPercent(value: number, fractionDigits = 1): string {
  // The server emits availability as a 0–1 ratio. Anything already >1 (a stray
  // pre-scaled percent) is treated as a percent, not multiplied again.
  const pct = value <= 1 ? value * 100 : value;
  return `${pct.toFixed(fractionDigits)}%`;
}

/** Format decimal hours as e.g. `244.6 h` (1 dp). */
export function formatHours(value: number): string {
  return `${value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
}

/** Format a count as a grouped integer (e.g. `1,204`). */
export function formatCount(value: number): string {
  return Math.round(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** Format money in the tenant currency (e.g. `€59,282`). */
export function formatCurrency(value: number, currency = DEFAULT_CURRENCY): string {
  try {
    return value.toLocaleString(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    });
  } catch {
    // Unknown/blank currency code → grouped number with a € prefix (EUR plant).
    return `€${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
}

/** Options for {@link formatReportValue}. */
export interface FormatReportValueOpts {
  /** Resolve a deviceId → device name. */
  deviceName?: NameResolver;
  /** Resolve a userId → user name. */
  userName?: NameResolver;
  /** Tenant currency (defaults to EUR). */
  currency?: string;
}

/**
 * Format a single report cell for DISPLAY, driven by its column `key`.
 *
 * Rules (in order):
 *  1. null → "" (empty) for non-id columns; "(unassigned)" for id columns.
 *  2. id columns (deviceId/userId/…) → resolve to a name; graceful fallback to
 *     a short id then "(unknown)" when no name is available.
 *  3. numeric columns → percent / hours / currency / grouped-count by kind.
 *  4. anything else (a string already shaped by the server, a date ISO string,
 *     a status) → returned via String() unchanged.
 */
export function formatReportValue(
  key: string,
  value: string | number | null,
  opts: FormatReportValueOpts = {},
): string {
  const idKind = idColumnKind(key);

  // ── id-reference columns ────────────────────────────────────────────────
  if (idKind) {
    if (value == null) return UNASSIGNED_LITERAL;
    const raw = String(value);
    // The server already emits "(unassigned)" for the null-device bucket (MTBF).
    if (raw === UNASSIGNED_LITERAL || raw === "") return UNASSIGNED_LITERAL;
    const resolver = idKind === "device" ? opts.deviceName : opts.userName;
    const name = resolver?.(raw) ?? null;
    if (name && name.trim()) return name;
    return shortenIdFallback(raw);
  }

  if (value == null) return "";

  // ── numeric columns by kind ─────────────────────────────────────────────
  if (typeof value === "number") {
    if (isRatioKey(key)) return formatPercent(value);
    if (isCurrencyKey(key)) return formatCurrency(value, opts.currency);
    if (isHoursKey(key)) return formatHours(value);
    // A bare count (failures, quantity, movements) → grouped integer.
    if (Number.isInteger(value)) return formatCount(value);
    // Other decimals (no recognised kind) → keep meaning, 2 dp, grouped.
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  // ── date/timestamp string columns ───────────────────────────────────────
  if (isDateKey(key)) return formatDate(value);

  // ── strings the server already shaped (titles, statuses, codes) ──────────
  return String(value);
}

/** A UUID/opaque id with no resolvable name → a short, non-ugly fallback. */
function shortenIdFallback(id: string): string {
  // UUID-like → first segment (e.g. `7f503f17`); otherwise the trimmed id.
  const head = id.split("-")[0] ?? id;
  if (head.length >= 6 && head.length <= 12) return head;
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

/**
 * Format a HEADLINE metric value by its server `unit` ("h" | "ratio" | "currency").
 * Returns the value string; the unit suffix is handled by the caller for "h" so
 * the existing layout (` h` appended) is preserved. ratio→percent and currency
 * are returned already-suffixed/prefixed (no extra unit text needed).
 */
export function formatMetricValue(
  value: number | null,
  unit: string | undefined,
  currency = DEFAULT_CURRENCY,
): { text: string; appendUnit: boolean } {
  if (value == null) return { text: "—", appendUnit: false };
  if (unit === "ratio") return { text: formatPercent(value), appendUnit: false };
  if (unit === "currency") return { text: formatCurrency(value, currency), appendUnit: false };
  if (unit === "h") return { text: value.toLocaleString(undefined, { maximumFractionDigits: 1 }), appendUnit: true };
  // No unit → a plain count/number, grouped.
  return { text: value.toLocaleString(undefined, { maximumFractionDigits: 2 }), appendUnit: !!unit };
}
